import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    ChangeEvent,
    KeyboardEvent,
} from 'react';
import { Range, Input, Flex, Text, FormControl } from '@landr/maestro';
import { Trans } from '@lingui/macro';

export interface useBpmInputsResult {
    displayRange: {
        min: string | number | '';
        max: string | number | '';
    };
    onMinBlur: () => void;
    updateMin: (event: ChangeEvent<HTMLInputElement>) => void;
    onMaxBlur: () => void;
    updateMax: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const replaceInputOnRange = (
    value: number,
    defaultValue: number,
    min: number,
    max: number,
) => {
    if (isNaN(value)) {
        value = defaultValue;
    } else if (value > max) {
        value = max;
    } else if (value < min) {
        value = min;
    }

    return value;
};

export const useBpmInputs = ({
    value,
    onChange,
    min,
    max,
    showMaxInput,
}: BpmInputProps) => {
    const [inputRange, setInputRange] = useState<{
        min: number | typeof NaN;
        max: number | typeof NaN;
    }>({ min: value.min, max: value.max ?? value.min });

    const displayRange = useMemo<{ min: number | ''; max: number | '' }>(
        () => ({
            min: isNaN(inputRange.min) ? '' : inputRange.min,
            max: isNaN(inputRange.max) ? '' : inputRange.max,
        }),
        [inputRange],
    );

    useEffect(
        () => setInputRange({ min: value.min, max: value.max ?? value.min }),
        [value],
    );

    const updateMin = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const min = parseInt(event.target.value, 10);

            setInputRange({ min, max: inputRange.max });
        },
        [inputRange, setInputRange],
    );

    const onMinBlur = useCallback(() => {
        const maximum =
            !showMaxInput || isNaN(inputRange.max) ? max : inputRange.max;
        const minValue = replaceInputOnRange(
            inputRange.min,
            value.min,
            min,
            maximum,
        );

        if (minValue !== inputRange.min) {
            setInputRange({ min: minValue, max: inputRange.max });
        }

        onChange({ min: minValue, max: inputRange.max });
    }, [
        inputRange.max,
        inputRange.min,
        max,
        min,
        onChange,
        showMaxInput,
        value.min,
    ]);

    const onMinKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.nativeEvent.isComposing) {
                return;
            }

            if (event.key === 'Enter') {
                onMinBlur();
            }
        },
        [onMinBlur],
    );

    const onMaxBlur = useCallback(() => {
        const minimum = isNaN(inputRange.min) ? min : inputRange.min;
        const maxValue = replaceInputOnRange(
            inputRange.max,
            value.max ?? value.min,
            minimum,
            max,
        );

        if (maxValue !== inputRange.max) {
            setInputRange({ min: inputRange.min, max: maxValue });
        }

        onChange({ min: inputRange.min, max: maxValue });
    }, [
        inputRange.min,
        inputRange.max,
        min,
        value.max,
        value.min,
        max,
        onChange,
    ]);

    const updateMax = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const max = parseInt(event.target.value, 10);

            setInputRange({ min: inputRange.min, max });
        },
        [inputRange, setInputRange],
    );

    const onMaxKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.nativeEvent.isComposing) {
                return;
            }

            if (event.key === 'Enter') {
                onMaxBlur();
            }
        },
        [onMaxBlur],
    );

    return {
        displayRange,
        onMinBlur,
        updateMin,
        onMinKeyDown,
        onMaxBlur,
        updateMax,
        onMaxKeyDown,
    };
};

export interface BpmInputProps {
    value: Range;
    min: number;
    max: number;
    onChange: (range: Partial<Range>) => void;
    showMaxInput: boolean;
}

export const BpmInputs: React.FC<BpmInputProps> = (props) => {
    const { min, max, showMaxInput } = props;
    const {
        displayRange,
        onMinBlur,
        updateMin,
        onMinKeyDown,
        onMaxBlur,
        updateMax,
        onMaxKeyDown,
    } = useBpmInputs(props);

    return (
        <Flex
            maxWidth={192}
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            height="md"
        >
            <FormControl width="xl" height="sm">
                <Input
                    value={displayRange.min.toString()}
                    size="md"
                    name="min"
                    min={min}
                    max={max}
                    onBlur={onMinBlur}
                    onChange={updateMin}
                    onKeyDown={onMinKeyDown}
                    inputMode="numeric"
                />
            </FormControl>
            {showMaxInput && (
                <>
                    <Text height="12px">
                        <Trans>to</Trans>
                    </Text>
                    <FormControl width="xl" height="sm">
                        <Input
                            value={displayRange.max.toString()}
                            size="md"
                            name="max"
                            min={min}
                            max={max}
                            onBlur={onMaxBlur}
                            onChange={updateMax}
                            onKeyDown={onMaxKeyDown}
                            inputMode="numeric"
                        />
                    </FormControl>
                </>
            )}
        </Flex>
    );
};
